/**
 * O pacote de Kick Off — a tela 11, e o fim do despejo do Google Forms.
 *
 * **O que ela substitui.** Hoje o consultor cola na descrição da issue `Kick Off` do ONB o
 * conteúdo bruto de um Google Forms: a ONB-3114 começa literalmente com "Carimbo de data/hora".
 * O gate declarado da F9 é *issue Kick Off aceita pelo consultor sem edição manual* — e é por
 * isso que a descrição consolidada, e não um `.docx`, é o artefato que importa.
 *
 * Quatro decisões desta tela:
 *
 * · **Gerar já tenta criar a issue.** Não existe botão separado de envio, e a ausência é decisão
 *   do backend que esta tela respeita: um segundo botão deixaria o consultor com o retrato pronto
 *   e nenhuma issue — que é exatamente o estado de hoje. Quando a tentativa não dá certo, o
 *   caminho é gerar de novo; entre uma coisa e outra as respostas podem ter mudado, e reenviar o
 *   retrato velho seria pior do que não enviar.
 *
 * · **Jira desligado é pendência, não erro.** As credenciais do Jira estão vazias neste servidor
 *   de propósito: nenhuma issue de teste foi criada no ONB real. O pacote se gera, se lê e se
 *   copia sem Jira nenhum; o que fica pendente é só o envio, e a tela o mostra com a frase que o
 *   servidor escreveu. Pintar isso de vermelho ensinaria quem opera a ignorar alerta.
 *
 * · **Desatualizado é o aviso mais gritante da tela.** Ele não quer dizer "existe versão mais
 *   nova" — isso é a ordem de `versao`. Quer dizer que alguma resposta mudou DEPOIS deste
 *   retrato, escrito pelo gatilho do banco. Sem ele o consultor copiaria uma foto velha e ninguém
 *   perceberia; com ele, a cópia fica bloqueada até a decisão ser consciente.
 *
 * · **Lacuna é conteúdo, não ausência.** Pergunta não respondida e trilha não enviada aparecem
 *   listadas, com o mesmo texto que vai escrito no corpo da issue. O silêncio no lugar do buraco
 *   é o defeito do formulário colado à mão que esta tela veio encerrar.
 *
 * **Dívida declarada:** `docx` e `xlsx` estão na spec §2.5 e não são gerados — cada um custaria
 * uma dependência nova e pesada, e o gate da fatia é a descrição. Nenhuma rota promete o que não
 * existe, e por isso esta tela também não oferece download.
 */

import { useState } from "react";
import { Link, useParams } from "react-router";
import {
  Aviso,
  Botao,
  Cabecalho,
  Cartao,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Segmentado,
  Tabela,
} from "../componentes/ui";
import { ErroDaApi } from "../nucleo/api";
import { useTitulo } from "../nucleo/titulo";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import {
  PERMISSAO_DE_GERAR_PACOTE,
  caminhoDaImplantacao,
  mensagemDoConsole,
  useEsteiraDaImplantacao,
} from "../nucleo/console";
import { formatarInstante } from "../nucleo/cofre";
import { caminhoDaRevisao } from "../nucleo/kickoff";
import { type ModoDoKickoff, useModoDoKickoff } from "../nucleo/marcosDoKickoff";
import {
  ROTULO_DO_FORMATO,
  ROTULO_DO_FORMATO_NO_GEOTASK,
  descricaoDo,
  estadoDoJira,
  rotuloDaLacuna,
  useGerarPacote,
  usePacote,
  usePacotes,
  type FormatoDaDescricao,
  type PacoteDetalhado,
} from "../nucleo/pacote";

function formatos(geotask: boolean) {
  const rotulos = geotask ? ROTULO_DO_FORMATO_NO_GEOTASK : ROTULO_DO_FORMATO;
  return [
    { valor: "adf", rotulo: rotulos.adf },
    { valor: "wiki_markup", rotulo: rotulos.wiki_markup },
  ] as const satisfies ReadonlyArray<{ valor: FormatoDaDescricao; rotulo: string }>;
}

export function PacoteDoKickoff() {
  const { id } = useParams();
  const implantacaoId = id ?? "";
  useTitulo("Resumo do Kick Off");

  const sessao = useSessao();
  const podeGerar = podeNaSessao(sessao.data, PERMISSAO_DE_GERAR_PACOTE);
  /* Com a K-O no GeoTask, o resumo é só retrato para consulta: gerar não cria tarefa. Só o modo
     confirmado do GeoTask muda a tela; carregando, em falha ou sem a consulta, vale o Jira, que é
     o que o servidor faz nesses casos. */
  const modo = useModoDoKickoff(implantacaoId, { habilitado: podeGerar });
  const geotask = modo === "geotask";
  /* A esteira serve só para o cabeçalho dizer de quem é este pacote. Ela vem do mesmo cache da
     tela da implantação, então quem chegou por lá não paga requisição nenhuma. */
  const esteira = useEsteiraDaImplantacao(implantacaoId);
  const organizacao = esteira.data?.organizacao;
  const nome = organizacao ? (organizacao.nome_fantasia ?? organizacao.razao_social) : null;

  return (
    <>
      <div className="cabecalho-da-trilha">
        <div>
          <h1 className="titulo-pagina">Resumo do Kick Off</h1>
          <p className="lead mb-2">
            {nome ? <>{nome} · </> : null}
            {geotask ? (
              "Resumo histórico das respostas do kickoff."
            ) : (
              <>O resumo de todas as respostas, pronto para a tarefa de Kick Off no Jira.</>
            )}
          </p>
        </div>
      </div>

      {!podeGerar ? (
        <Aviso tom="marca" data-testid="sem-pacote-gerar">
          <b>O resumo do Kick Off é gerado e lido pela consultoria.</b> Se você precisa dele, peça
          a quem conduz a implantação.
        </Aviso>
      ) : (
        <ConteudoDoPacote implantacaoId={implantacaoId} modo={modo} />
      )}

      <p className="nota-rodape mt-5">
        <Link to={caminhoDaImplantacao(implantacaoId, "kickoff")}>Voltar à implantação</Link> ·{" "}
        <Link to={caminhoDaRevisao(implantacaoId)}>Ver as respostas do kickoff</Link>
      </p>
    </>
  );
}

export function ConteudoDoPacote({
  implantacaoId,
  modo,
}: {
  implantacaoId: string;
  /** O caminho da K-O; `undefined` enquanto carrega ou se falhou, e então vale o Jira. */
  modo: ModoDoKickoff | undefined;
}) {
  /* Só o GeoTask confirmado esconde o fluxo do Jira: o servidor cria a K-O no Jira fora dele. */
  const geotask = modo === "geotask";
  const [formato, setFormato] = useState<FormatoDaDescricao>("adf");
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const pacotes = usePacotes(implantacaoId);
  const geracao = useGerarPacote(implantacaoId);

  if (!pacotes.data) {
    if (pacotes.isError) {
      return (
        <Aviso tom="erro" data-testid="falha-dos-pacotes">
          {pacotes.error instanceof ErroDaApi
            ? pacotes.error.message
            : "Não foi possível carregar os resumos agora."}
        </Aviso>
      );
    }
    return (
      <p className="lead" role="status">
        Carregando as versões do resumo…
      </p>
    );
  }

  const versoes = pacotes.data.pacotes;
  const ultimo = versoes[0] ?? null;
  /* A versão aberta é a última gerada até alguém escolher outra. Ela é a que o consultor usa; as
     antigas ficam legíveis porque um retrato que não se pode reabrir não serve de prova. */
  const abertoId = escolhido ?? ultimo?.id ?? null;

  return (
    <>
      {/* A releitura depois de gerar pode falhar com a lista já na tela: o aviso soma-se ao que
          está exibido, não o substitui — a versão gerada não pode sumir por causa dele. */}
      {pacotes.isError ? (
        <Aviso tom="erro" className="mb-5" data-testid="falha-dos-pacotes">
          {pacotes.error instanceof ErroDaApi
            ? pacotes.error.message
            : "Não foi possível atualizar a lista de resumos agora."}{" "}
          O que aparece abaixo pode estar desatualizado.
        </Aviso>
      ) : null}

      <Cartao titulo="Gerar uma versão" className="mb-5" data-testid="gerar-pacote">
        <p className="cartao__texto mb-3">
          Gerar guarda o retrato de agora: as respostas enviadas, o que ficou em branco e as
          divergências entre trilhas.{" "}
          {modo === "geotask" ? (
            <>
              Esta versão é um registro para consulta; a K-O é gerada na revisão das respostas,
              depois do aceite.
            </>
          ) : modo === undefined ? (
            <>Esta versão é um registro para consulta.</>
          ) : (
            <>
              <b>Ao gerar, o portal também tenta criar a tarefa de Kick Off no Jira</b> desta
              implantação; se o Jira não responder, o resumo fica salvo e a pendência aparece aqui.
            </>
          )}
        </p>

        <p className="campo__rotulo">Formato do texto</p>
        <Segmentado<FormatoDaDescricao>
          rotulo="Formato do texto"
          itens={formatos(geotask)}
          valor={formato}
          aoMudar={setFormato}
          className="mb-3"
        />
        <p className="campo__ajuda mb-3">
          {geotask ? (
            "Para consultar fora do portal, escolha o formato legível com título e lista."
          ) : (
            <>
              Para colar à mão na tarefa, use &ldquo;Para colar no Jira&rdquo;: título, lista e
              negrito chegam intactos.
            </>
          )}
        </p>

        {geracao.isError ? (
          <Aviso tom="erro" className="mb-3" data-testid="erro-da-geracao">
            {mensagemDoConsole(geracao.error)}
          </Aviso>
        ) : null}

        <div className="acoes-do-console">
          <Botao
            data-testid="gerar-pacote-agora"
            disabled={geracao.isPending}
            onClick={() =>
              geracao.mutate(formato, { onSuccess: (pacote) => setEscolhido(pacote.id) })
            }
          >
            {geracao.isPending
              ? "Gerando…"
              : versoes.length === 0
                ? geotask
                  ? "Gerar resumo"
                  : "Gerar o resumo e criar a tarefa"
                : "Gerar uma versão nova"}
          </Botao>
        </div>
      </Cartao>

      {versoes.length === 0 ? (
        <Aviso tom="marca" data-testid="sem-pacotes">
          <b>Nenhum resumo gerado para esta implantação ainda.</b>
        </Aviso>
      ) : (
        <>
          <h2 className="titulo-secao">Versões</h2>
          {/* O histórico fica: a versão 2 não apaga a 1. Guardar as duas é o que permite
              responder "o que foi colado no Jira naquele dia?" — pergunta que o despejo do
              Google Forms nunca soube responder. */}
          <div data-testid="versoes-do-pacote">
          <Tabela legenda="Versões do resumo, da mais nova para a mais antiga.">
            <Cabecalho>
              <Linha>
                <CelulaCabecalho>Versão</CelulaCabecalho>
                <CelulaCabecalho>Gerada</CelulaCabecalho>
                <CelulaCabecalho>Retrato</CelulaCabecalho>
                <CelulaCabecalho>Lacunas</CelulaCabecalho>
                <CelulaCabecalho>{geotask ? "Tarefa legada" : "Tarefa no Jira"}</CelulaCabecalho>
                <CelulaCabecalho>Texto</CelulaCabecalho>
              </Linha>
            </Cabecalho>
            <Corpo>
              {versoes.map((pacote) => {
                const jira = estadoDoJira(pacote);
                return (
                  <Linha key={pacote.id}>
                    <Celula className="celula-nome numero-tabular">v{pacote.versao}</Celula>
                    <Celula className="celula-secundaria numero-tabular">
                      {formatarInstante(pacote.gerado_em) ?? "—"}
                      <div>{pacote.gerado_por?.nome ?? "—"}</div>
                    </Celula>
                    <Celula>
                      <Chip
                        estado={pacote.desatualizado ? "erro" : "ok"}
                        data-testid={`retrato-v${pacote.versao}`}
                      >
                        {pacote.desatualizado ? "desatualizado" : "em dia"}
                      </Chip>
                    </Celula>
                    <Celula className="numero-tabular">{pacote.lacunas.length}</Celula>
                    <Celula className="celula-secundaria">
                      {jira.estado === "enviado" ? (
                        <>
                          <code>{jira.chave}</code>
                          <div>{formatarInstante(jira.quando) ?? ""}</div>
                        </>
                      ) : jira.estado === "pendente" ? (
                        <Chip estado="aguardando" semPonto>
                          pendente
                        </Chip>
                      ) : (
                        "—"
                      )}
                    </Celula>
                    <Celula>
                      <Botao
                        variante="fantasma"
                        tamanho="pequeno"
                        data-testid={`abrir-v${pacote.versao}`}
                        onClick={() => setEscolhido(pacote.id)}
                      >
                        {pacote.id === abertoId ? "Aberta" : "Abrir"}
                      </Botao>
                    </Celula>
                  </Linha>
                );
              })}
            </Corpo>
          </Tabela>
          </div>

          {abertoId ? <VersaoAberta pacoteId={abertoId} geotask={geotask} /> : null}
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ uma versão */

function VersaoAberta({ pacoteId, geotask }: { pacoteId: string; geotask: boolean }) {
  const pacote = usePacote(pacoteId);

  if (pacote.isPending) {
    return (
      <p className="lead mt-5" role="status">
        Carregando o resumo…
      </p>
    );
  }

  if (pacote.isError) {
    return (
      <Aviso tom="erro" className="mt-5" data-testid="falha-do-pacote">
        {pacote.error instanceof ErroDaApi
          ? pacote.error.message
          : "Não foi possível abrir esta versão agora."}
      </Aviso>
    );
  }

  return <Versao pacote={pacote.data} geotask={geotask} />;
}

function Versao({ pacote, geotask }: { pacote: PacoteDetalhado; geotask: boolean }) {
  const descricao = descricaoDo(pacote);
  const jira = estadoDoJira(pacote);

  return (
    <section
      className="mt-5"
      aria-labelledby="titulo-da-versao"
      data-testid="versao-do-pacote"
      data-versao={String(pacote.versao)}
      data-desatualizado={pacote.desatualizado ? "sim" : "nao"}
    >
      <h2 className="titulo-secao" id="titulo-da-versao">
        Versão {pacote.versao}
      </h2>

      {/* O aviso vem ANTES do texto, e não como rodapé: o gesto que esta tela existe para
          servir é copiar, e um consultor que rola até a descrição já copiou. */}
      {pacote.desatualizado ? (
        <Aviso tom="erro" className="mb-4" data-testid="pacote-desatualizado">
          <b>Este retrato está desatualizado.</b> Alguma resposta do cliente mudou depois de ele
          ser gerado
          {pacote.desatualizado_em ? (
            <> (em {formatarInstante(pacote.desatualizado_em)})</>
          ) : null}
          . O texto abaixo descreve o kickoff de antes da alteração —{" "}
          <b>{geotask ? "não use como versão atual" : "não copie para o Jira"}</b> sem gerar uma
          versão nova.
        </Aviso>
      ) : null}

      {/* Com a K-O no GeoTask, o cartão do Jira só aparece para o pacote antigo que teve issue. */}
      {!geotask || pacote.jira_kickoff_key ? <Cartao
        titulo="Tarefa de Kick Off no Jira"
        className="mb-4"
        data-testid="jira-do-pacote"
        data-estado-do-jira={jira.estado}
        acessorio={
          <Chip
            estado={
              jira.estado === "enviado" ? "ok" : jira.estado === "pendente" ? "aguardando" : "neutro"
            }
          >
            {jira.estado === "enviado"
              ? "criada"
              : jira.estado === "pendente"
                ? "pendente"
                : "sem tentativa"}
          </Chip>
        }
      >
        {jira.estado === "enviado" ? (
          <p className="cartao__texto">
            A tarefa <code>{jira.chave}</code> foi criada
            {jira.quando ? <> em {formatarInstante(jira.quando)}</> : null} a partir deste resumo.
            Ela é uma só por implantação: gerar de novo não cria outra.
          </p>
        ) : jira.estado === "pendente" ? (
          <>
            <p className="cartao__texto">
              <b>A tarefa de Kick Off ainda não foi criada no Jira.</b>
            </p>
            <p className="cartao__texto mt-3">
              {/* "pronto e completo" era afirmado mesmo com lacuna aberta — a tela dizia o
                  contrário do próprio bloco de lacunas logo abaixo. O retrato pode estar
                  completo ou não; o que sempre vale é que ele está pronto para ser colado. */}
              {pacote.lacunas.length === 0
                ? "O resumo está completo: copie o texto abaixo e cole na tarefa."
                : `O resumo está salvo, com ${pacote.lacunas.length} ${
                    pacote.lacunas.length === 1 ? "lacuna listada" : "lacunas listadas"
                  } logo abaixo: copie o texto e cole na tarefa — as lacunas vão escritas nele.`}{" "}
              Quando o Jira voltar a responder, gerar uma versão nova cria a tarefa sozinho.
            </p>
            {/* O motivo do servidor, inteiro — só a API sabe qual credencial falta. Fica recolhido
                porque é diagnóstico para quem pode corrigir, não o texto de uso (spec 4.4). */}
            <details className="mt-3" data-testid="detalhes-tecnicos-do-jira">
              <summary className="campo__rotulo cursor-pointer">Detalhes técnicos</summary>
              <p className="cartao__texto mt-2" data-testid="pendencia-do-jira">
                {jira.motivo}
              </p>
            </details>
          </>
        ) : (
          <p className="cartao__texto">
            Nenhuma tentativa de envio registrada para esta versão.
          </p>
        )}
      </Cartao> : null}

      <Lacunas pacote={pacote} geotask={geotask} />

      <Cartao
        titulo="Texto do resumo"
        data-testid="descricao-do-pacote"
        acessorio={
          descricao?.formato ? (
            <Chip estado="neutro" semPonto>
              {(geotask ? ROTULO_DO_FORMATO_NO_GEOTASK : ROTULO_DO_FORMATO)[descricao.formato]}
            </Chip>
          ) : null
        }
      >
        {!descricao?.texto ? (
          <Aviso tom="atencao" data-testid="pacote-sem-descricao">
            Esta versão não tem o texto do resumo salvo. Gere o resumo de novo.
          </Aviso>
        ) : (
          <>
            <p className="cartao__texto mb-3">
              {geotask ? (
                <>Este é um retrato das respostas <b>no dia em que o resumo foi gerado</b>.</>
              ) : (
                <>
                  É este o texto que vai (ou foi) para a tarefa, como ficou <b>no dia em que foi
                  gerado</b>.
                </>
              )}{" "}
              {descricao.caracteres !== null ? (
                <span className="numero-tabular">{descricao.caracteres} caracteres.</span>
              ) : null}
            </p>
            <Copia texto={descricao.texto} conteudo={descricao.conteudo} pacote={pacote} geotask={geotask} />
            <pre className="pacote__descricao" data-testid="texto-do-pacote">
              {descricao.texto}
            </pre>
          </>
        )}
      </Cartao>
    </section>
  );
}

function Lacunas({ pacote, geotask }: { pacote: PacoteDetalhado; geotask: boolean }) {
  if (pacote.lacunas.length === 0) {
    return (
      <Aviso tom="sucesso" className="mb-4" data-testid="pacote-sem-lacunas">
        Nenhuma lacuna neste retrato: todas as trilhas foram enviadas e nenhuma pergunta
        obrigatória ficou sem resposta.
      </Aviso>
    );
  }

  return (
    <Cartao
      titulo={`${pacote.lacunas.length} lacuna${pacote.lacunas.length === 1 ? "" : "s"} no retrato`}
      className="mb-4"
      data-testid="lacunas-do-pacote"
      acessorio={<Chip estado="atencao">{pacote.lacunas.length}</Chip>}
    >
      <p className="cartao__texto mb-3">
        {geotask ? <>Elas ficam <b>registradas no resumo</b></> : <>Elas vão <b>escritas no texto da tarefa</b></>}:
        pergunta obrigatória não respondida e trilha não enviada aparecem com todas as letras. Pergunta opcional em branco não é lacuna.
      </p>
      <ul className="lista-de-impedimentos">
        {pacote.lacunas.map((lacuna, indice) => (
          <li key={`${lacuna.motivo}-${lacuna.trilha ?? ""}-${lacuna.pergunta_id ?? indice}`}>
            {/* Só o motivo e a frase do servidor, que já nomeia trilha e pergunta por extenso. A
                chave da trilha e o id da pergunta são implementação (spec 4.4). */}
            <b>{rotuloDaLacuna(lacuna.motivo)}</b>
            <div className="celula-secundaria">{lacuna.descricao}</div>
          </li>
        ))}
      </ul>
    </Cartao>
  );
}

/**
 * Copiar a descrição — o gesto que a tela existe para servir enquanto o Jira estiver desligado.
 *
 * **Dois botões, e não um.** O que se cola numa issue pela interface do Jira é o texto legível; o
 * ADF cru só serve para quem vai alimentar a API. Oferecer só um dos dois faria alguém colar JSON
 * num campo de descrição, que é o tipo de erro que a pessoa só descobre depois de salvar.
 *
 * **Retrato desatualizado não se copia.** É a mesma recusa que o servidor faz no envio ao Jira, e
 * pelo mesmo motivo: copiar é o ato que leva o retrato velho para fora do portal. Deixar copiar
 * com um aviso ao lado transformaria a proteção em decoração.
 *
 * A área de transferência pode não estar disponível — contexto inseguro, permissão negada. Nesse
 * caso a tela diz para selecionar o texto abaixo, que continua inteiro na página: um botão que
 * falha em silêncio faz a pessoa colar o que estava na área de transferência antes.
 */
function Copia({
  texto,
  conteudo,
  pacote,
  geotask,
}: {
  texto: string;
  conteudo: string | null;
  pacote: PacoteDetalhado;
  geotask: boolean;
}) {
  const estruturada = geotask ? "Versão estruturada" : "Versão para envio automático";
  const [aviso, setAviso] = useState<{ tom: "sucesso" | "erro"; frase: string } | null>(null);

  async function copiar(valor: string, oque: string) {
    try {
      await navigator.clipboard.writeText(valor);
      setAviso({ tom: "sucesso", frase: `${oque} para a área de transferência.` });
    } catch {
      setAviso({
        tom: "erro",
        frase:
          "O navegador não liberou a área de transferência. Selecione o texto abaixo e copie à mão — ele está inteiro na tela.",
      });
    }
  }

  if (pacote.desatualizado) {
    return (
      <p className="nota-rodape mb-3" data-testid="copia-bloqueada">
        A cópia está desligada nesta versão porque o resumo está desatualizado. Gere uma versão
        nova — é ela que descreve o que o cliente respondeu.
      </p>
    );
  }

  return (
    <>
      <div className="acoes-do-console mb-3">
        <Botao tamanho="pequeno" data-testid="copiar-texto" onClick={() => void copiar(texto, "Texto copiado")}>
          Copiar o texto
        </Botao>
        {conteudo && conteudo !== texto ? (
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            data-testid="copiar-conteudo"
            onClick={() => void copiar(conteudo, `${estruturada} copiada`)}
          >
            {geotask ? "Copiar a versão estruturada" : "Copiar a versão para envio automático"}
          </Botao>
        ) : null}
      </div>
      {aviso ? (
        <Aviso tom={aviso.tom} className="mb-3" data-testid="aviso-da-copia">
          {aviso.frase}
        </Aviso>
      ) : null}
    </>
  );
}
